import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { QUERIES } from '@/constants/queries'
import type { MatchCoursesFormType } from '@/schemas/education/match-courses-schema'
import { getAllCoursesByCurriculum } from '@/services/education/get-all-courses-by-curriculum'
import { getAllCurriculumsByCourse } from '@/services/education/get-all-curriculums-by-course'
import { denyRequest } from '@/services/requests/deny-request'
import { getPreviousEducationCoursesByRequest } from '@/services/requests/get-previous-education-courses-by-request'
import { getRequestById } from '@/services/requests/get-request-by-id'
import { grantRequest } from '@/services/requests/grant-request'
import type { Curriculum } from '@/types/courses/curriculum'
import type { DesiredEducationCourse } from '@/types/education/desired-education-course'

export const useCreditTransferReview = () => {
  const [currentCurriculum, setCurrentCurriculum] = useState<Curriculum>()
  const [selectedCollegeCourses, setSelectedCollegeCourses] = useState<
    DesiredEducationCourse[]
  >([])
  const [isOpenGrantDialog, setIsOpenGrantDialog] = useState(false)
  const [isOpenNotGrantDialog, setIsOpenNotGrantDialog] = useState(false)

  const { id } = useParams()

  const {
    data: request,
    isLoading: isRequestLoading,
    refetch: refetchRequest
  } = useQuery({
    queryKey: [QUERIES.QUERY_GET_REQUEST_BY_ID, id],
    queryFn: () => getRequestById(id!),
    enabled: !!id
  })

  const { data: curriculumCourses, isLoading: isCurriculumCoursesLoading } =
    useQuery({
      queryKey: [QUERIES.QUERY_GET_COURSES_BY_CURRICULUM, currentCurriculum],
      queryFn: () => getAllCoursesByCurriculum(currentCurriculum?.id),
      enabled: !!currentCurriculum
    })

  const { data: previousCourses, isLoading: isPreviouCoursesLoading } =
    useQuery({
      queryKey: [QUERIES.QUERY_GET_PREVIOUS_EDUCATION_COURSES_BY_REQUEST, id],
      queryFn: () => getPreviousEducationCoursesByRequest(id!),
      enabled: !!id
    })

  const {
    mutate: grantMutate,
    isPending: grantIsPending,
    isSuccess: grantSuccess
  } = useMutation({
    mutationKey: [QUERIES.QUERY_POST_GRANT_CREDIT_TRANSFER],
    mutationFn: (data: MatchCoursesFormType) => grantRequest(id!, data),
    onSuccess: refetchRequest
  })

  const {
    mutate: denyMutate,
    isPending: denyIsPending,
    isSuccess: denySuccess
  } = useMutation({
    mutationKey: [QUERIES.QUERY_POST_DENY_CREDIT_TRANSFER],
    mutationFn: (data: MatchCoursesFormType) => denyRequest(id!, data),
    onSuccess: refetchRequest
  })

  const { data: allCurriculums, isLoading: isAllCurriculumsLoading } = useQuery(
    {
      queryKey: [QUERIES.QUERY_GET_CURRICULUMS, request],
      queryFn: () => getAllCurriculumsByCourse(request?.student.courseId),
      enabled: !!request
    }
  )

  const isLoading =
    isRequestLoading
    || isAllCurriculumsLoading
    || isCurriculumCoursesLoading
    || isPreviouCoursesLoading

  useEffect(() => {
    if (allCurriculums && request) {
      const current = allCurriculums.find(
        (curriculum) => curriculum.id === request.desiredEducation.curriculum
      )
      setCurrentCurriculum(current)
    }
  }, [allCurriculums, request])

  return {
    states: {
      isLoading,
      request,
      allCurriculums,
      currentCurriculum,
      curriculumCourses,
      selectedCollegeCourses,
      previousCourses,
      isOpenGrantDialog,
      grantIsPending,
      grantSuccess,
      denyIsPending,
      denySuccess,
      isOpenNotGrantDialog
    },
    actions: {
      setCurrentCurriculum,
      setSelectedCollegeCourses,
      setIsOpenGrantDialog,
      grantMutate,
      denyMutate,
      setIsOpenNotGrantDialog
    }
  }
}
